import { Entity } from '@debbie/common/dist/index.js';
import { caseGroupScopeIds, } from '@debbie/common/dist/properties/index.js';
import { useCaseGroups } from '@debbie/cortex/dist/case-groups/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { usePropertyTypes } from '@debbie/cortex/dist/properties/index.js';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../theme.js';
import { Chevron, DropPanel, FilterChips, PALETTE, Row, RowDescription, RowLabel, SearchField, Trigger, TriggerClearSpace, TriggerLabel, TriggerMeta, } from './SearchableDropdown.js';
import SearchableSelect from './SearchableSelect.js';
const propertyTypeId = (propertyType) => propertyType.propertyTypeId;
const propertyLabel = (propertyType) => propertyType.label;
const propertyRef = (propertyType) => propertyType.ref;
const PropertyTypeSelect = (props) => {
    const lang = useSelector((s) => s.lang);
    const propertyTypes = usePropertyTypes();
    const caseGroupsLoadable = useCaseGroups();
    const caseGroups = h.hasData(caseGroupsLoadable)
        ? caseGroupsLoadable.data
        : null;
    const caseGroupLabels = useMemo(() => new Map(caseGroups?.map((caseGroup) => [caseGroup.id, caseGroup.label])), [caseGroups]);
    const facet = useMemo(() => ({
        of: propertyRef,
        options: [
            Entity.Creditor,
            Entity.Customer,
            Entity.Case,
            Entity.CaseVoucher,
        ].map((ref) => ({ key: ref, label: lang.entities[ref] })),
    }), [lang]);
    const scopeLabels = (propertyType) => caseGroupScopeIds(propertyType.scopes)
        .map((id) => caseGroupLabels.get(id) ?? id)
        .join(', ');
    const metadata = (propertyType) => `${lang.entities[propertyType.ref]} · ${lang.propertyDataTypes[propertyType.type]}`;
    const scopeDescription = (propertyType) => propertyType.scopes.length === 0
        ? null
        : `${lang.properties.scope}: ${scopeLabels(propertyType)}`;
    const description = (propertyType) => (React.createElement(Description, null,
        React.createElement(Metadata, null,
            React.createElement(EntityBadge, null, lang.entities[propertyType.ref]),
            React.createElement("span", null, lang.propertyDataTypes[propertyType.type])),
        propertyType.scopes.length > 0 && (React.createElement(Scope, null, scopeDescription(propertyType)))));
    return (React.createElement(PropertySelect, { ...props, placeholder: props.placeholder ?? lang.properties.selectProperty, items: propertyTypes, getId: propertyTypeId, getLabel: propertyLabel, getSearchText: (propertyType) => `${propertyType.label} ${propertyType.handle} ${propertyType.description ?? ''} ${metadata(propertyType)} ${propertyType.type} ${scopeLabels(propertyType)}`, getDescription: description, getTriggerMeta: metadata, getRowAttributes: (propertyType) => ({
            title: [
                propertyType.label,
                metadata(propertyType),
                scopeDescription(propertyType),
            ]
                .filter((text) => text !== null)
                .join('\n'),
        }), facet: facet, labels: {
            search: lang.properties.searchProperties,
            all: lang.properties.properties,
        }, recentsStorageKey: "debbie.propertyTypeSelect.recentlyUsed" }));
};
export default PropertyTypeSelect;
const Description = styled.span `
  display: flex;
  flex-direction: column;
  gap: 6px;
  white-space: normal;
  overflow-wrap: anywhere;
`;
const Metadata = styled.span `
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;
const EntityBadge = styled.span `
  padding: 2px 6px;
  border-radius: 4px;
  background: ${PALETTE.accent.background};
  color: ${PALETTE.accent.color};
  font-weight: 600;
`;
const Scope = styled.span `
  color: ${THEME.TEXT_MUTED};
  line-height: 1.5;
`;
const PropertySelect = styled(SearchableSelect) `
  min-width: 0;

  ${Trigger} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    column-gap: 8px;
    row-gap: 4px;
  }

  ${TriggerLabel} {
    grid-column: 1;
    grid-row: 1;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  ${TriggerMeta} {
    grid-column: 1;
    grid-row: 2;
    text-align: left;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  ${TriggerClearSpace} {
    grid-column: 2;
    grid-row: 1 / span 2;
  }

  ${Chevron} {
    grid-column: 3;
    grid-row: 1 / span 2;
  }

  ${DropPanel} {
    position: relative;
    top: auto;
    min-width: 0;
    box-sizing: border-box;
    margin-top: 6px;
  }

  ${SearchField} {
    min-width: 0;
  }

  ${FilterChips} {
    flex-wrap: wrap;
  }

  ${Row} {
    padding: 12px 14px;
  }

  ${Row} + ${Row} {
    border-top: 1px solid ${THEME.BORDER};
  }

  ${RowLabel} {
    font-weight: 600;
    line-height: 1.4;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  ${RowDescription} {
    margin-top: 5px;
    white-space: normal;
  }
`;
//# sourceMappingURL=PropertyTypeSelect.js.map